import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { VisualizerContext } from "./visualizer/visualizerShared";
import { TwelvePointsOverlay } from "./visualizer/TwelvePointsOverlay";
import { WinnerOverlay } from "./visualizer/WinnerOverlay";
import { Top6Overlay } from "./visualizer/Top6Overlay";
import { Trophy, Home, Smartphone, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Voter artist photo/video for the side voting column.
// If the media fails to load (e.g. missing upload -> 404) or there is no media,
// the whole block collapses so the votes rise up and stay visible.
const VoterArtistMedia = ({ mediaUrl, isVideo, sizeMode, sizePx, showVoterBorder, primaryColor, defaultArtistPhoto, resolveUrl, fromName, videoRef }) => {
  const [failed, setFailed] = useState(false);
  const [usingDefault, setUsingDefault] = useState(false);
  if (!mediaUrl || failed) return null;
  const containerStyle = sizeMode === 'fixed'
    ? { width: `${sizePx}px`, maxWidth: '100%', margin: '0 auto' }
    : {};
  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.1 }}
      className={sizeMode === 'fixed' ? 'w-full flex justify-center' : 'w-full'}
    >
      <div className={`overflow-hidden ${sizeMode === 'adaptive' ? 'w-full' : ''}`} style={containerStyle}>
        {isVideo ? (
          <video
            ref={videoRef}
            src={mediaUrl}
            autoPlay
            playsInline
            className="w-full h-auto block"
            style={showVoterBorder ? { borderBottom: `4px solid ${primaryColor}` } : {}}
            onError={() => setFailed(true)}
            data-testid="voter-artist-video"
          />
        ) : (
          <img
            src={mediaUrl}
            alt={`Artista de ${fromName}`}
            className="w-full h-auto block"
            style={showVoterBorder ? { borderBottom: `4px solid ${primaryColor}` } : {}}
            onError={(e) => {
              if (defaultArtistPhoto && !usingDefault) {
                setUsingDefault(true);
                e.target.src = resolveUrl(defaultArtistPhoto);
              } else {
                setFailed(true);
              }
            }}
            data-testid="voter-artist-photo"
          />
        )}
      </div>
    </motion.div>
  );
};


// Dev-only logging helpers (silenced in production builds)
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (isDev) console.log(...args); };
const devError = (...args) => { if (isDev) console.error(...args); };
const devDebug = (...args) => { if (isDev) console.debug(...args); };

// Desktop reference resolution. On mobile the whole visualizer is rendered at this
// size and scaled down to fit (and rotated if the phone is in portrait), so it looks
// EXACTLY like the desktop screen.
const DESIGN_W = 1920;
const DESIGN_H = 1080;

// Helper function to resolve URLs - handles multiple URL formats
const resolveUrl = (url) => {
  if (!url) return "";
  // If it's a data URL, return as-is
  if (url.startsWith('data:')) return url;
  // If it's a full URL, fix domain and path issues
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Extract the path from any preview URL that has /api/uploads/ and rebuild with current domain
    const uploadsMatch = url.match(/\/api\/uploads\/.+$/);
    if (uploadsMatch) {
      return `${BACKEND_URL}${uploadsMatch[0]}`;
    }
    // Fix legacy URLs: replace /uploads/ with /api/uploads/
    const legacyMatch = url.match(/(\/uploads\/.+)$/);
    if (legacyMatch) {
      return `${BACKEND_URL}/api${legacyMatch[1]}`;
    }
    return url;
  }
  // Handle relative paths
  if (url.startsWith('/uploads/') && !url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

const Visualizer = () => {
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentVoter, setCurrentVoter] = useState(null);
  const [currentVote, setCurrentVote] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [countries, setCountries] = useState([]);
  const [voteApplied, setVoteApplied] = useState(false);
  const [backgroundVideo, setBackgroundVideo] = useState("");
  const [bgVideoBlur, setBgVideoBlur] = useState(10);
  const [bgVideoBrightness, setBgVideoBrightness] = useState(100);
  const [bgVideoOpacity, setBgVideoOpacity] = useState(20);
  const [fontTitle, setFontTitle] = useState("Unbounded");
  const [fontSubtitle, setFontSubtitle] = useState("Inter");
  const [fontCountry, setFontCountry] = useState("Inter");
  const [fontPoints, setFontPoints] = useState("Chivo Mono");
  const [fontVoting, setFontVoting] = useState("Inter");
  const [fontTitleItalic, setFontTitleItalic] = useState(false);
  const [fontSubtitleItalic, setFontSubtitleItalic] = useState(false);
  const [fontCountryItalic, setFontCountryItalic] = useState(false);
  const [fontPointsItalic, setFontPointsItalic] = useState(false);
  const [fontVotingItalic, setFontVotingItalic] = useState(false);
  const [fontVotingSecondaryItalic, setFontVotingSecondaryItalic] = useState(false);
  // Custom fonts for each section
  const [customFontTitleUrl, setCustomFontTitleUrl] = useState("");
  const [customFontTitleName, setCustomFontTitleName] = useState("");
  const [customFontSubtitleUrl, setCustomFontSubtitleUrl] = useState("");
  const [customFontSubtitleName, setCustomFontSubtitleName] = useState("");
  const [customFontCountryUrl, setCustomFontCountryUrl] = useState("");
  const [customFontCountryName, setCustomFontCountryName] = useState("");
  const [customFontPointsUrl, setCustomFontPointsUrl] = useState("");
  const [customFontPointsName, setCustomFontPointsName] = useState("");
  const [customFontVotingUrl, setCustomFontVotingUrl] = useState("");
  const [customFontVotingName, setCustomFontVotingName] = useState("");
  const [customFontsLoaded, setCustomFontsLoaded] = useState({});
  const [primaryColor, setPrimaryColor] = useState("#EC4899");
  const [secondaryColor, setSecondaryColor] = useState("#22D3EE");
  const [tertiaryColor, setTertiaryColor] = useState("#8B5CF6");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPosition, setLogoPosition] = useState("top-left");
  const [logoSize, setLogoSize] = useState(80);
  const [logoZIndex, setLogoZIndex] = useState(50);
  const [logoOpacity, setLogoOpacity] = useState(100);
  const [logoX, setLogoX] = useState(null);
  const [logoY, setLogoY] = useState(null);
  const [extraImages, setExtraImages] = useState([
    { url: "", position: "top-right", size: 80, zIndex: 50, opacity: 100, x: null, y: null },
    { url: "", position: "bottom-left", size: 80, zIndex: 50, opacity: 100, x: null, y: null },
    { url: "", position: "bottom-right", size: 80, zIndex: 50, opacity: 100, x: null, y: null }
  ]);
  // New states for font sizes and row colors
  const [fontSizeTitle, setFontSizeTitle] = useState(48);
  const [fontSizeSubtitle, setFontSizeSubtitle] = useState(16);
  const [fontSizeCountry, setFontSizeCountry] = useState(14);
  const [fontSizePoints, setFontSizePoints] = useState(24);
  const [fontSizeVoting, setFontSizeVoting] = useState(20);
  const [rowBgEven, setRowBgEven] = useState("rgba(24, 24, 27, 0.9)");
  const [rowBgOdd, setRowBgOdd] = useState("rgba(39, 39, 42, 0.7)");
  const [rowBgEvenTranslucent, setRowBgEvenTranslucent] = useState(false);
  const [rowBgOddTranslucent, setRowBgOddTranslucent] = useState(false);
  const [rowBadgeTranslucent, setRowBadgeTranslucent] = useState(false);
  const [defaultArtistPhoto, setDefaultArtistPhoto] = useState("");
  const [artistPhotoSizeMode, setArtistPhotoSizeMode] = useState("adaptive");
  const [artistPhotoSizePx, setArtistPhotoSizePx] = useState(300);
  // Customizable texts
  const [textTitle, setTextTitle] = useState("CLASIFICACIÓN EN VIVO");
  const [textSubtitle, setTextSubtitle] = useState("Sistema de Votación Eurovisión");
  const [textVotingNow, setTextVotingNow] = useState("VOTANDO AHORA");
  const [textWaiting, setTextWaiting] = useState("Esperando próxima votación...");
  // Sound effect for 12 points
  const [sound12PointsUrl, setSound12PointsUrl] = useState("");
  const [soundWinnerUrl, setSoundWinnerUrl] = useState("");
  // Confetti effect
  const [confettiEnabled, setConfettiEnabled] = useState(true);
  const [confettiColors, setConfettiColors] = useState(["#EC4899", "#22D3EE", "#8B5CF6", "#FFD700", "#FF6B6B"]);
  // Top 3 custom colors
  const [top1Color, setTop1Color] = useState("#EC4899");
  const [top2Color, setTop2Color] = useState("#8B5CF6");
  const [top3Color, setTop3Color] = useState("#22D3EE");
  const [lastPlaceColor, setLastPlaceColor] = useState("#8B2020");
  const [top1Translucent, setTop1Translucent] = useState(true);
  const [top2Translucent, setTop2Translucent] = useState(true);
  const [top3Translucent, setTop3Translucent] = useState(true);
  const [lastPlaceTranslucent, setLastPlaceTranslucent] = useState(false);
  const [positionBadgeTranslucent, setPositionBadgeTranslucent] = useState(false);
  // Scoreboard layout
  const [votingColumnCenter, setVotingColumnCenter] = useState(false);
  const [leftColSpan, setLeftColSpan] = useState(4);
  const [rightColSpan, setRightColSpan] = useState(4);
  const [votingColSpan, setVotingColSpan] = useState(4);
  const [columnGap, setColumnGap] = useState(6);
  const [votingWidthCustom, setVotingWidthCustom] = useState(false);
  const [votingWidthSpan, setVotingWidthSpan] = useState(4);
  const [voteBarColorMode, setVoteBarColorMode] = useState("original");
  const [voteBarColor1, setVoteBarColor1] = useState("#18181b");
  const [voteBarColor2, setVoteBarColor2] = useState("#27272a");
  const [voteBarTranslucent, setVoteBarTranslucent] = useState(false);
  const [voteBarOpacity, setVoteBarOpacity] = useState(100);
  // Subtitle counter
  const [subtitleCounterEnabled, setSubtitleCounterEnabled] = useState(false);
  const [subtitlePhaseOrder, setSubtitlePhaseOrder] = useState("jury_first");
  const [subtitleJuryLabel, setSubtitleJuryLabel] = useState("Jurado Profesional");
  const [subtitleTelevoteLabel, setSubtitleTelevoteLabel] = useState("Televoto");
  const [revealedVoters, setRevealedVoters] = useState([]);
  const [mystery12Text, setMystery12Text] = useState("?????????");
  const [mysteryHiddenPoints, setMysteryHiddenPoints] = useState("12");
  const [overlay12CustomFontUrl, setOverlay12CustomFontUrl] = useState("");
  const [winnerCustomFontUrl, setWinnerCustomFontUrl] = useState("");
  const [twelveRevealed, setTwelveRevealed] = useState(false);
  const [voteDisplayColumns, setVoteDisplayColumns] = useState("single");
  const [showVoterBorder, setShowVoterBorder] = useState(true);
  const [showJuryBadge, setShowJuryBadge] = useState(true);
  const [fontVotingSecondary, setFontVotingSecondary] = useState("Inter");
  const [customFontVotingSecondaryUrl, setCustomFontVotingSecondaryUrl] = useState("");
  const [customFontVotingSecondaryName, setCustomFontVotingSecondaryName] = useState("");
  // 12-points overlay
  const [overlay12Enabled, setOverlay12Enabled] = useState(true);
  const [overlay12DurationMode, setOverlay12DurationMode] = useState("auto");
  const [overlay12DurationSeconds, setOverlay12DurationSeconds] = useState(8);
  const [overlay12BgStyle, setOverlay12BgStyle] = useState("gradient");
  const [overlay12BgColor, setOverlay12BgColor] = useState("#000000");
  const [overlay12BgOpacity, setOverlay12BgOpacity] = useState(80);
  const [overlay12TextReceives, setOverlay12TextReceives] = useState("OTORGA 12 PUNTOS A");
  const [overlay12UseAdminColors, setOverlay12UseAdminColors] = useState(true);
  const [overlay12ArrowColor, setOverlay12ArrowColor] = useState("");
  const [overlay12ArrowThickness, setOverlay12ArrowThickness] = useState(12);
  const [overlay12ArrowHeadSize, setOverlay12ArrowHeadSize] = useState(46);
  const [overlay12ShowConfetti, setOverlay12ShowConfetti] = useState(true);
  const [overlay12StyleCustom, setOverlay12StyleCustom] = useState(false);
  const [overlay12FontTitle, setOverlay12FontTitle] = useState("Unbounded");
  const [overlay12FontCountry, setOverlay12FontCountry] = useState("Inter");
  const [overlay12FontSecondary, setOverlay12FontSecondary] = useState("Inter");
  const [overlay12SizeScale, setOverlay12SizeScale] = useState("m");
  const [overlay12Visible, setOverlay12Visible] = useState(false);
  const [overlay12RecipientId, setOverlay12RecipientId] = useState(null);
  const [overlay12Stage, setOverlay12Stage] = useState(0); // 0=hidden,1=giver-only,2=both
  // Winner overlay
  const [winnerOverlayEnabled, setWinnerOverlayEnabled] = useState(true);
  const [winnerOverlayDurationMode, setWinnerOverlayDurationMode] = useState("manual");
  const [winnerOverlayDurationSeconds, setWinnerOverlayDurationSeconds] = useState(15);
  const [winnerOverlayBgStyle, setWinnerOverlayBgStyle] = useState("gradient");
  const [winnerOverlayBgColor, setWinnerOverlayBgColor] = useState("#000000");
  const [winnerOverlayBgOpacity, setWinnerOverlayBgOpacity] = useState(90);
  const [winnerOverlayTitleText, setWinnerOverlayTitleText] = useState("¡ENHORABUENA!");
  const [winnerOverlaySubtitleText, setWinnerOverlaySubtitleText] = useState("GANADOR");
  const [winnerOverlayUseAdminColors, setWinnerOverlayUseAdminColors] = useState(true);
  const [winnerOverlayAccentColor, setWinnerOverlayAccentColor] = useState("");
  const [winnerOverlayIcon, setWinnerOverlayIcon] = useState("trophy");
  const [winnerOverlayIconUrl, setWinnerOverlayIconUrl] = useState("");
  const [winnerOverlayShowConfetti, setWinnerOverlayShowConfetti] = useState(true);
  const [winnerOverlayShowPoints, setWinnerOverlayShowPoints] = useState(true);
  const [winnerOverlayStyleCustom, setWinnerOverlayStyleCustom] = useState(false);
  const [winnerOverlayFontTitle, setWinnerOverlayFontTitle] = useState("Unbounded");
  const [winnerOverlayFontCountry, setWinnerOverlayFontCountry] = useState("Inter");
  const [winnerOverlayFontSecondary, setWinnerOverlayFontSecondary] = useState("Inter");
  const [winnerOverlaySizeScale, setWinnerOverlaySizeScale] = useState("m");
  const [winnerOverlayVisible, setWinnerOverlayVisible] = useState(false);
  const [top6Visible, setTop6Visible] = useState(false);
  const [top6Entries, setTop6Entries] = useState([]);
  const [top6TitleText, setTop6TitleText] = useState("Current top 6:");
  const [top6StyleCustom, setTop6StyleCustom] = useState(false);
  const [top6FontTitle, setTop6FontTitle] = useState("Unbounded");
  const [top6FontCountry, setTop6FontCountry] = useState("Inter");
  const [winnerOverlayWinnerId, setWinnerOverlayWinnerId] = useState(null);
  const [winnerOverlayWinnerPoints, setWinnerOverlayWinnerPoints] = useState(0);
  // Voting status
  const [allVotesApplied, setAllVotesApplied] = useState(false);

  // Force-landscape + scale-to-fit on mobile so the visualizer looks EXACTLY like
  // the desktop screen (rendered at DESIGN_W x DESIGN_H, then scaled and rotated).
  const [orientation, setOrientation] = useState({ mobile: false, rotate: false, w: 0, h: 0, scale: 1, offsetX: 0, offsetY: 0 });
  const [showRotateHint, setShowRotateHint] = useState(false);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isTouch = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window;
      const isMobile = isTouch || Math.min(w, h) <= 600;
      const isPortrait = h > w;
      const rotate = isMobile && isPortrait;
      // Landscape "stage area" available on screen (after rotation if portrait).
      const areaW = rotate ? h : w;
      const areaH = rotate ? w : h;
      const scale = isMobile ? Math.min(areaW / DESIGN_W, areaH / DESIGN_H) : 1;
      const offsetX = (areaW - DESIGN_W * scale) / 2;
      const offsetY = (areaH - DESIGN_H * scale) / 2;
      setOrientation({ mobile: isMobile, rotate, w, h, scale, offsetX, offsetY });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, []);

  // Show a one-time "rotate your phone" hint the first time we are in rotated (portrait) mode.
  useEffect(() => {
    if (orientation.rotate && !showRotateHint && !sessionStorage.getItem('rotateHintShown')) {
      setShowRotateHint(true);
      sessionStorage.setItem('rotateHintShown', '1');
      const t = setTimeout(() => setShowRotateHint(false), 4500);
      return () => clearTimeout(t);
    }
  }, [orientation.rotate, showRotateHint]);

  const videoRef = useRef(null);
  const audioRef = useRef(null);

  // Ref callback for the voter (artist/jury) video: play once WITH sound by default.
  // If the browser blocks autoplay-with-sound (no prior user gesture), fall back to muted
  // playback so the video still shows instead of freezing.
  const playVoterVideoWithSound = (el) => {
    if (!el) return;
    el.muted = false;
    el.volume = 1;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        el.muted = true;
        el.play().catch(() => {});
      });
    }
  };
  
  // Force video to play when backgroundVideo changes
  useEffect(() => {
    if (videoRef.current && backgroundVideo) {
      const video = videoRef.current;
      // Only reload if the source actually changed
      const currentSrc = video.querySelector('source')?.src || "";
      const expectedSrc = resolveUrl(backgroundVideo);
      if (!currentSrc.endsWith(new URL(expectedSrc, window.location.origin).pathname)) {
        video.load();
      }
      video.play().catch(() => {});
    }
  }, [backgroundVideo]);

  // Refs to avoid stale closures in polling callbacks
  const winnerAudioRef = useRef(null);
  const sound12PointsUrlRef = useRef("");
  const confettiEnabledRef = useRef(true);
  const confettiColorsRef = useRef(["#EC4899", "#22D3EE", "#8B5CF6", "#FFD700", "#FF6B6B"]);

  // Keep refs in sync with state
  useEffect(() => { sound12PointsUrlRef.current = sound12PointsUrl; }, [sound12PointsUrl]);

  // Try to unlock audio automatically (works in kiosk/OBS or if the browser
  // remembers prior interaction). If blocked, any click/key on the page unlocks it.
  useEffect(() => {
    if (audioUnlocked || !sound12PointsUrl) return;
    const tryUnlock = () => {
      const el = audioRef.current || winnerAudioRef.current;
      if (!el) return;
      const p = el.play();
      if (p) p.then(() => {
        el.pause();
        el.currentTime = 0;
        setAudioUnlocked(true);
      }).catch(() => {});
    };
    tryUnlock();
    document.addEventListener('pointerdown', tryUnlock);
    document.addEventListener('keydown', tryUnlock);
    return () => {
      document.removeEventListener('pointerdown', tryUnlock);
      document.removeEventListener('keydown', tryUnlock);
    };
  }, [audioUnlocked, sound12PointsUrl, soundWinnerUrl]);
  useEffect(() => { confettiEnabledRef.current = confettiEnabled; }, [confettiEnabled]);
  const overlay12ShowConfettiRef = useRef(true);
  useEffect(() => { overlay12ShowConfettiRef.current = overlay12ShowConfetti; }, [overlay12ShowConfetti]);
  useEffect(() => { confettiColorsRef.current = confettiColors; }, [confettiColors]);

  // Play sound and confetti when 12 points are revealed (detect false→true transition)
  const prevTwelveFromServerRef = useRef(false);
  const prevOverlayVisibleRef = useRef(false);
  const overlayAutoCloseTimerRef = useRef(null);
  const overlayStageTimerRef = useRef(null);
  
  const triggerTwelveRevealEffects = () => {
    if (sound12PointsUrlRef.current && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => devLog('Audio autoplay blocked:', e.message));
    }
    // Fire confetti if enabled
    if (confettiEnabledRef.current) {
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
      
      const randomInRange = (min, max) => Math.random() * (max - min) + min;
      const colors = confettiColorsRef.current;
      
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        const particleCount = 80 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors
        });
      }, 250);
    }
  };

  // Single one-shot confetti splash for the 12-points overlay (fires once when the
  // recipient is revealed). Unlike the winner overlay, this is NOT continuous.
  const triggerOverlay12Confetti = () => {
    if (!overlay12ShowConfettiRef.current) return;
    const colors = confettiColorsRef.current;
    confetti({ particleCount: 90, spread: 85, startVelocity: 48, origin: { x: 0.2, y: 0.85 }, colors, zIndex: 9999 });
    confetti({ particleCount: 90, spread: 85, startVelocity: 48, origin: { x: 0.8, y: 0.85 }, colors, zIndex: 9999 });
    confetti({ particleCount: 60, spread: 120, startVelocity: 40, origin: { x: 0.5, y: 0.9 }, colors, zIndex: 9999 });
  };

  const pollRef = useRef(null);
  const fetchCurrentVoteRef = useRef(null);

  useEffect(() => {
    if (pollRef.current) pollRef.current();
    const interval = setInterval(() => {
      if (pollRef.current) pollRef.current();
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Auto-close winner overlay if mode is auto
  useEffect(() => {
    if (winnerOverlayVisible && winnerOverlayDurationMode === "auto") {
      if (winnerAutoCloseTimerRef.current) clearTimeout(winnerAutoCloseTimerRef.current);
      winnerAutoCloseTimerRef.current = setTimeout(async () => {
        try { await axios.post(`${API}/admin/hide-winner-overlay`); } catch (e) { devDebug('No se pudo ocultar el overlay del ganador (auto):', e?.message); }
      }, Math.max(1, winnerOverlayDurationSeconds) * 1000);
    }
    return () => {
      if (!winnerOverlayVisible && winnerAutoCloseTimerRef.current) {
        clearTimeout(winnerAutoCloseTimerRef.current);
      }
    };
  }, [winnerOverlayVisible, winnerOverlayDurationMode, winnerOverlayDurationSeconds]);

  // Schedule auto-close once overlay reaches stage 2 (both flags visible)
  useEffect(() => {
    if (overlay12Stage === 2 && overlay12DurationMode === "auto" && overlay12Visible) {
      if (overlayAutoCloseTimerRef.current) clearTimeout(overlayAutoCloseTimerRef.current);
      overlayAutoCloseTimerRef.current = setTimeout(async () => {
        try {
          await axios.post(`${API}/admin/hide-12-overlay`);
        } catch (e) { devError("Error auto-closing overlay", e); }
      }, Math.max(1, overlay12DurationSeconds) * 1000);
    }
    return () => {
      if (overlay12Stage !== 2 && overlayAutoCloseTimerRef.current) {
        clearTimeout(overlayAutoCloseTimerRef.current);
      }
    };
  }, [overlay12Stage, overlay12DurationMode, overlay12DurationSeconds, overlay12Visible]);

  // Load custom fonts for each section
  useEffect(() => {
    const loadCustomFont = async (url, name, section) => {
      if (!url || !name) return;
      
      const resolvedUrl = resolveUrl(url);
      const ext = url.toLowerCase();
      const format = ext.endsWith('.woff2') ? 'woff2' : 
                     ext.endsWith('.woff') ? 'woff' : 
                     ext.endsWith('.ttf') ? 'truetype' : 'opentype';
      
      // Remove old style if exists
      const oldStyle = document.getElementById(`custom-font-style-${section}`);
      if (oldStyle) oldStyle.remove();
      
      // Create @font-face style
      const style = document.createElement('style');
      style.id = `custom-font-style-${section}`;
      style.textContent = `
        @font-face {
          font-family: '${name}';
          src: url('${resolvedUrl}') format('${format}');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
      
      // Load font using Font Loading API
      try {
        const font = new FontFace(name, `url('${resolvedUrl}')`);
        const loadedFont = await font.load();
        document.fonts.add(loadedFont);
        devLog(`Custom font loaded for ${section}:`, name);
        setCustomFontsLoaded(prev => ({ ...prev, [section]: true }));
      } catch (error) {
        devError(`Failed to load custom font for ${section}:`, error);
      }
    };
    
    loadCustomFont(customFontTitleUrl, customFontTitleName, 'title');
    loadCustomFont(customFontSubtitleUrl, customFontSubtitleName, 'subtitle');
    loadCustomFont(customFontCountryUrl, customFontCountryName, 'country');
    loadCustomFont(customFontPointsUrl, customFontPointsName, 'points');
    loadCustomFont(customFontVotingUrl, customFontVotingName, 'voting');
    loadCustomFont(customFontVotingSecondaryUrl, customFontVotingSecondaryName, 'votingSecondary');
    loadCustomFont(overlay12CustomFontUrl, 'Overlay12CustomFont', 'overlay12');
    loadCustomFont(winnerCustomFontUrl, 'WinnerCustomFont', 'winnerOverlay');
  }, [customFontTitleUrl, customFontTitleName, customFontSubtitleUrl, customFontSubtitleName,
      customFontCountryUrl, customFontCountryName, customFontPointsUrl, customFontPointsName, 
      customFontVotingUrl, customFontVotingName, customFontVotingSecondaryUrl, customFontVotingSecondaryName,
      overlay12CustomFontUrl, winnerCustomFontUrl]);

  const voterCountryId = currentVoter?.country_id;
  const voterShowAnimation = currentVoter?.show_animation;
  useEffect(() => {
    if (voterCountryId && voterShowAnimation) {
      if (fetchCurrentVoteRef.current) fetchCurrentVoteRef.current(voterCountryId);
      setShowAnimation(true);
    } else {
      setShowAnimation(false);
      setCurrentVote(null);
    }
  }, [voterCountryId, voterShowAnimation]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      devError("Error fetching leaderboard", error);
    }
  };

  const fetchCurrentVoter = async () => {
    try {
      const response = await axios.get(`${API}/admin/current-voter`);
      if (response.data.country_id) {
        setCurrentVoter(response.data);
        const newTwelveRevealed = response.data.twelve_revealed || false;
        const newOverlayVisible = response.data.twelve_overlay_visible || false;
        const newRecipientId = response.data.twelve_overlay_recipient_id || null;
        
        // Detect transition from false to true → trigger sound + confetti
        if (newTwelveRevealed && !prevTwelveFromServerRef.current) {
          triggerTwelveRevealEffects();
        }
        prevTwelveFromServerRef.current = newTwelveRevealed;
        setTwelveRevealed(newTwelveRevealed);

        // Overlay transition: false → true
        if (newOverlayVisible && !prevOverlayVisibleRef.current) {
          setOverlay12RecipientId(newRecipientId);
          setOverlay12Visible(true);
          setOverlay12Stage(1);
          // Clear pending timers
          if (overlayStageTimerRef.current) clearTimeout(overlayStageTimerRef.current);
          if (overlayAutoCloseTimerRef.current) clearTimeout(overlayAutoCloseTimerRef.current);
          // After 1.5s, transition to stage 2 (recipient appears)
          overlayStageTimerRef.current = setTimeout(() => {
            setOverlay12Stage(2);
            // Single confetti splash when the recipient of the 12 points is revealed
            triggerOverlay12Confetti();
          }, 1500);
        }
        // Overlay closed by admin
        if (!newOverlayVisible && prevOverlayVisibleRef.current) {
          setOverlay12Visible(false);
          setOverlay12Stage(0);
          if (overlayStageTimerRef.current) clearTimeout(overlayStageTimerRef.current);
          if (overlayAutoCloseTimerRef.current) clearTimeout(overlayAutoCloseTimerRef.current);
        }
        prevOverlayVisibleRef.current = newOverlayVisible;
      }
    } catch (error) {
      devError("Error fetching current voter", error);
    }
  };

  const lastCountriesRef = useRef("");
  
  const fetchCountries = async () => {
    try {
      const [countriesRes, juryRes] = await Promise.all([
        axios.get(`${API}/countries`),
        axios.get(`${API}/admin/countries`)
      ]);
      const newData = JSON.stringify(juryRes.data);
      if (newData === lastCountriesRef.current) return;
      lastCountriesRef.current = newData;
      setCountries(juryRes.data);
    } catch (error) {
      devError("Error fetching countries", error);
    }
  };

  const lastBgConfigRef = useRef("");
  
  const fetchBackground = async () => {
    try {
      const response = await axios.get(`${API}/background`);
      const data = response.data;
      // Only update state if config actually changed
      const configHash = JSON.stringify(data);
      if (configHash === lastBgConfigRef.current) return;
      lastBgConfigRef.current = configHash;
      
      setBackgroundVideo(data.video_url || "");
      setBgVideoBlur(data.bg_video_blur ?? 10);
      setBgVideoBrightness(data.bg_video_brightness ?? 100);
      setBgVideoOpacity(data.bg_video_opacity ?? 20);
      setFontTitle(data.font_title || "Unbounded");
      setFontSubtitle(data.font_subtitle || "Inter");
      setFontCountry(data.font_country || "Inter");
      setFontPoints(data.font_points || "Chivo Mono");
      setFontVoting(data.font_voting || "Inter");
      setFontTitleItalic(data.font_title_italic === true);
      setFontSubtitleItalic(data.font_subtitle_italic === true);
      setFontCountryItalic(data.font_country_italic === true);
      setFontPointsItalic(data.font_points_italic === true);
      setFontVotingItalic(data.font_voting_italic === true);
      setFontVotingSecondaryItalic(data.font_voting_secondary_italic === true);
      // Custom fonts for each section
      setCustomFontTitleUrl(data.custom_font_title_url || "");
      setCustomFontTitleName(data.custom_font_title_name || "");
      setCustomFontSubtitleUrl(data.custom_font_subtitle_url || "");
      setCustomFontSubtitleName(data.custom_font_subtitle_name || "");
      setCustomFontCountryUrl(data.custom_font_country_url || "");
      setCustomFontCountryName(data.custom_font_country_name || "");
      setCustomFontPointsUrl(data.custom_font_points_url || "");
      setCustomFontPointsName(data.custom_font_points_name || "");
      setCustomFontVotingUrl(data.custom_font_voting_url || "");
      setCustomFontVotingName(data.custom_font_voting_name || "");
      setPrimaryColor(data.primary_color || "#EC4899");
      setSecondaryColor(data.secondary_color || "#22D3EE");
      setTertiaryColor(data.tertiary_color || "#8B5CF6");
      setLogoUrl(data.logo_url || "");
      setLogoPosition(data.logo_position || "top-left");
      setLogoSize(data.logo_size || 80);
      setLogoZIndex(data.logo_z_index ?? 50);
      setLogoOpacity(data.logo_opacity ?? 100);
      setLogoX(data.logo_x ?? null);
      setLogoY(data.logo_y ?? null);
      setExtraImages([
        { url: data.extra_image_1_url || "", position: data.extra_image_1_position || "top-right", size: data.extra_image_1_size || 80, zIndex: data.extra_image_1_z_index ?? 50, opacity: data.extra_image_1_opacity ?? 100, x: data.extra_image_1_x ?? null, y: data.extra_image_1_y ?? null },
        { url: data.extra_image_2_url || "", position: data.extra_image_2_position || "bottom-left", size: data.extra_image_2_size || 80, zIndex: data.extra_image_2_z_index ?? 50, opacity: data.extra_image_2_opacity ?? 100, x: data.extra_image_2_x ?? null, y: data.extra_image_2_y ?? null },
        { url: data.extra_image_3_url || "", position: data.extra_image_3_position || "bottom-right", size: data.extra_image_3_size || 80, zIndex: data.extra_image_3_z_index ?? 50, opacity: data.extra_image_3_opacity ?? 100, x: data.extra_image_3_x ?? null, y: data.extra_image_3_y ?? null }
      ]);
      // New font size and row color settings
      setFontSizeTitle(data.font_size_title || 48);
      setFontSizeSubtitle(data.font_size_subtitle || 16);
      setFontSizeCountry(data.font_size_country || 14);
      setFontSizePoints(data.font_size_points || 24);
      setFontSizeVoting(data.font_size_voting || 20);
      setRowBgEven(data.row_bg_even || "rgba(24, 24, 27, 0.9)");
      setRowBgOdd(data.row_bg_odd || "rgba(39, 39, 42, 0.7)");
      setRowBgEvenTranslucent(data.row_bg_even_translucent === true);
      setRowBgOddTranslucent(data.row_bg_odd_translucent === true);
      setRowBadgeTranslucent(data.row_badge_translucent === true);
      setDefaultArtistPhoto(data.default_artist_photo || "");
      setArtistPhotoSizeMode(data.artist_photo_size_mode || "adaptive");
      setArtistPhotoSizePx(data.artist_photo_size_px || 300);
      // Customizable texts
      setTextTitle(data.text_title || "CLASIFICACIÓN EN VIVO");
      setTextSubtitle(data.text_subtitle || "Sistema de Votación Eurovisión");
      setTextVotingNow(data.text_voting_now || "VOTANDO AHORA");
      setTextWaiting(data.text_waiting || "Esperando próxima votación...");
      // Sound effect
      setSound12PointsUrl(data.sound_12_points_url || "");
      setSoundWinnerUrl(data.sound_winner_url || "");
      // Confetti
      setConfettiEnabled(data.confetti_enabled !== false);
      setConfettiColors(data.confetti_colors || ["#EC4899", "#22D3EE", "#8B5CF6", "#FFD700", "#FF6B6B"]);
      setTop1Color(data.top1_color || "#EC4899");
      setTop2Color(data.top2_color || "#8B5CF6");
      setTop3Color(data.top3_color || "#22D3EE");
      setLastPlaceColor(data.last_place_color || "#8B2020");
      setTop1Translucent(data.top1_translucent !== false);
      setTop2Translucent(data.top2_translucent !== false);
      setTop3Translucent(data.top3_translucent !== false);
      setLastPlaceTranslucent(data.last_place_translucent === true);
      setPositionBadgeTranslucent(data.position_badge_translucent === true);
      // Scoreboard layout
      setVotingColumnCenter(data.voting_column_center === true);
      setLeftColSpan(data.left_column_span || 4);
      setRightColSpan(data.right_column_span || 4);
      setVotingColSpan(data.voting_column_span || 4);
      setColumnGap(data.column_gap ?? 6);
      setVotingWidthCustom(data.voting_width_custom === true);
      setVotingWidthSpan(data.voting_width_span ?? 4);
      setVoteBarColorMode(data.vote_bar_color_mode || "original");
      setVoteBarColor1(data.vote_bar_color_1 || "#18181b");
      setVoteBarColor2(data.vote_bar_color_2 || "#27272a");
      setVoteBarTranslucent(data.vote_bar_translucent === true);
      setVoteBarOpacity(data.vote_bar_opacity ?? 100);
      // Subtitle counter
      setSubtitleCounterEnabled(data.subtitle_counter_enabled === true);
      setSubtitlePhaseOrder(data.subtitle_phase_order || "jury_first");
      setSubtitleJuryLabel(data.subtitle_jury_label || "Jurado Profesional");
      setSubtitleTelevoteLabel(data.subtitle_televote_label || "Televoto");
      setMystery12Text(data.mystery_12_text || "?????????");
      setMysteryHiddenPoints(data.mystery_hidden_points || "12");
      setOverlay12CustomFontUrl(data.overlay_12_custom_font_url || "");
      setWinnerCustomFontUrl(data.winner_overlay_custom_font_url || "");
      setTop6TitleText(data.top6_title_text || "Current top 6:");
      setTop6StyleCustom(data.top6_style_custom || false);
      setTop6FontTitle(data.top6_font_title || "Unbounded");
      setTop6FontCountry(data.top6_font_country || "Inter");
      setVoteDisplayColumns(data.vote_display_columns || "single");
      setShowVoterBorder(data.show_voter_border !== false);
      setShowJuryBadge(data.show_jury_badge !== false);
      setFontVotingSecondary(data.font_voting_secondary || "Inter");
      setCustomFontVotingSecondaryUrl(data.custom_font_voting_secondary_url || "");
      setCustomFontVotingSecondaryName(data.custom_font_voting_secondary_name || "");
      // 12-points overlay
      setOverlay12Enabled(data.overlay_12_enabled !== false);
      setOverlay12DurationMode(data.overlay_12_duration_mode || "auto");
      setOverlay12DurationSeconds(data.overlay_12_duration_seconds ?? 8);
      setOverlay12BgStyle(data.overlay_12_bg_style || "gradient");
      setOverlay12BgColor(data.overlay_12_bg_color || "#000000");
      setOverlay12BgOpacity(data.overlay_12_bg_opacity ?? 80);
      setOverlay12TextReceives(data.overlay_12_text_receives || "OTORGA 12 PUNTOS A");
      setOverlay12UseAdminColors(data.overlay_12_use_admin_colors !== false);
      setOverlay12ArrowColor(data.overlay_12_arrow_color || "");
      setOverlay12ArrowThickness(data.overlay_12_arrow_thickness ?? 12);
      setOverlay12ArrowHeadSize(data.overlay_12_arrow_head_size ?? 46);
      setOverlay12ShowConfetti(data.overlay_12_show_confetti !== false);
      setOverlay12StyleCustom(data.overlay_12_style_custom === true);
      setOverlay12FontTitle(data.overlay_12_font_title || "Unbounded");
      setOverlay12FontCountry(data.overlay_12_font_country || "Inter");
      setOverlay12FontSecondary(data.overlay_12_font_secondary || "Inter");
      setOverlay12SizeScale(data.overlay_12_size_scale || "m");
      // Winner overlay
      setWinnerOverlayEnabled(data.winner_overlay_enabled !== false);
      setWinnerOverlayDurationMode(data.winner_overlay_duration_mode || "manual");
      setWinnerOverlayDurationSeconds(data.winner_overlay_duration_seconds ?? 15);
      setWinnerOverlayBgStyle(data.winner_overlay_bg_style || "gradient");
      setWinnerOverlayBgColor(data.winner_overlay_bg_color || "#000000");
      setWinnerOverlayBgOpacity(data.winner_overlay_bg_opacity ?? 90);
      setWinnerOverlayTitleText(data.winner_overlay_title_text || "¡ENHORABUENA!");
      setWinnerOverlaySubtitleText(data.winner_overlay_subtitle_text || "GANADOR");
      setWinnerOverlayUseAdminColors(data.winner_overlay_use_admin_colors !== false);
      setWinnerOverlayAccentColor(data.winner_overlay_accent_color || "");
      setWinnerOverlayIcon(data.winner_overlay_icon || "trophy");
      setWinnerOverlayIconUrl(data.winner_overlay_icon_url || "");
      setWinnerOverlayShowConfetti(data.winner_overlay_show_confetti !== false);
      setWinnerOverlayShowPoints(data.winner_overlay_show_points !== false);
      setWinnerOverlayStyleCustom(data.winner_overlay_style_custom === true);
      setWinnerOverlayFontTitle(data.winner_overlay_font_title || "Unbounded");
      setWinnerOverlayFontCountry(data.winner_overlay_font_country || "Inter");
      setWinnerOverlayFontSecondary(data.winner_overlay_font_secondary || "Inter");
      setWinnerOverlaySizeScale(data.winner_overlay_size_scale || "m");
    } catch (error) {
      devError("Error fetching background", error);
    }
  };

  const fetchCurrentVote = async (countryId) => {
    try {
      const response = await axios.get(`${API}/country/vote-status/${countryId}`);
      if (response.data.vote) {
        setCurrentVote(response.data.vote);
        setVoteApplied(response.data.vote.applied || false);
      }
    } catch (error) {
      devError("Error fetching vote", error);
    }
  };

  const fetchVotingStatus = async () => {
    try {
      const response = await axios.get(`${API}/voting-status`);
      setAllVotesApplied(response.data.all_complete || false);
    } catch (error) {
      devError("Error fetching voting status", error);
    }
  };

  const lastRevealedRef = useRef("");
  const fetchRevealedVoters = async () => {
    try {
      const response = await axios.get(`${API}/revealed-voters`);
      const newData = JSON.stringify(response.data.voters);
      if (newData === lastRevealedRef.current) return;
      lastRevealedRef.current = newData;
      setRevealedVoters(response.data.voters || []);
    } catch (error) {
      devError("Error fetching revealed voters", error);
    }
  };

  // Winner overlay polling
  const prevWinnerVisibleRef = useRef(false);
  const winnerAutoCloseTimerRef = useRef(null);
  const winnerConfettiIntervalRef = useRef(null);

  const triggerWinnerConfetti = () => {
    if (winnerConfettiIntervalRef.current) clearInterval(winnerConfettiIntervalRef.current);
    const colors = confettiColorsRef.current;
    const fire = () => {
      confetti({ particleCount: 80, spread: 100, startVelocity: 45, origin: { x: 0.1, y: 0.9 }, colors, zIndex: 10001 });
      confetti({ particleCount: 80, spread: 100, startVelocity: 45, origin: { x: 0.9, y: 0.9 }, colors, zIndex: 10001 });
      confetti({ particleCount: 60, spread: 360, startVelocity: 30, origin: { x: 0.5, y: 0.3 }, colors, zIndex: 10001 });
    };
    fire();
    winnerConfettiIntervalRef.current = setInterval(fire, 1500);
  };

  const stopWinnerConfetti = () => {
    if (winnerConfettiIntervalRef.current) {
      clearInterval(winnerConfettiIntervalRef.current);
      winnerConfettiIntervalRef.current = null;
    }
  };

  const fetchWinnerOverlay = async () => {
    try {
      const response = await axios.get(`${API}/winner-overlay`);
      const newVisible = response.data.visible || false;
      setWinnerOverlayWinnerId(response.data.winner_id || null);
      setWinnerOverlayWinnerPoints(response.data.winner_points || 0);
      if (newVisible && !prevWinnerVisibleRef.current) {
        setWinnerOverlayVisible(true);
        if (winnerOverlayShowConfetti) triggerWinnerConfetti();
        if (winnerAudioRef.current) {
          winnerAudioRef.current.currentTime = 0;
          winnerAudioRef.current.play().catch(e => devLog('Winner audio blocked:', e.message));
        }
      }
      if (!newVisible && prevWinnerVisibleRef.current) {
        setWinnerOverlayVisible(false);
        stopWinnerConfetti();
        if (winnerAudioRef.current) {
          winnerAudioRef.current.pause();
          winnerAudioRef.current.currentTime = 0;
        }
      }
      prevWinnerVisibleRef.current = newVisible;
    } catch (error) {
      devError("Error fetching winner overlay", error);
    }
  };

  const fetchTop6Overlay = async () => {
    try {
      const response = await axios.get(`${API}/top6-overlay`);
      setTop6Visible(response.data.visible || false);
      setTop6Entries(response.data.entries || []);
    } catch (error) {
      devError("Error fetching top6 overlay", error);
    }
  };

  // Keep refs pointing to the latest fetch closures (used by the polling
  // interval and the voter-change effect) so their useEffect deps stay stable.
  pollRef.current = () => {
    fetchLeaderboard();
    fetchCurrentVoter();
    fetchVotingStatus();
    fetchCountries();
    fetchBackground();
    fetchRevealedVoters();
    fetchWinnerOverlay();
    fetchTop6Overlay();
  };
  fetchCurrentVoteRef.current = fetchCurrentVote;

  // Compute the dynamic subtitle based on revealed voters and phases
  const computeDynamicSubtitle = () => {
    if (!subtitleCounterEnabled || countries.length === 0) return textSubtitle;

    const juryCountries = countries.filter(c => c.is_jury);
    const televoteCountries = countries.filter(c => !c.is_jury);
    // All countries that can vote (both jury + non-jury vote)
    // But jury and televote are different pools for the counter
    const juryIds = new Set(juryCountries.map(c => c.id));
    const televoteIds = new Set(televoteCountries.map(c => c.id));

    const revealedJury = revealedVoters.filter(id => juryIds.has(id));
    const revealedTelevote = revealedVoters.filter(id => televoteIds.has(id));

    const totalJury = juryCountries.length;
    const totalTelevote = televoteCountries.length;

    if (subtitlePhaseOrder === "all_together") {
      // No phases, just a global counter
      const total = totalJury + totalTelevote;
      const current = revealedVoters.length;
      if (current === 0) return textSubtitle;
      return `${textSubtitle}: ${current}/${total}`;
    }

    // Determine current phase
    let firstPhaseLabel, firstPhaseRevealed, firstPhaseTotal;
    let secondPhaseLabel, secondPhaseRevealed, secondPhaseTotal;

    if (subtitlePhaseOrder === "jury_first") {
      firstPhaseLabel = subtitleJuryLabel;
      firstPhaseRevealed = revealedJury.length;
      firstPhaseTotal = totalJury;
      secondPhaseLabel = subtitleTelevoteLabel;
      secondPhaseRevealed = revealedTelevote.length;
      secondPhaseTotal = totalTelevote;
    } else {
      firstPhaseLabel = subtitleTelevoteLabel;
      firstPhaseRevealed = revealedTelevote.length;
      firstPhaseTotal = totalTelevote;
      secondPhaseLabel = subtitleJuryLabel;
      secondPhaseRevealed = revealedJury.length;
      secondPhaseTotal = totalJury;
    }

    // If no one revealed yet, show default
    if (revealedVoters.length === 0) return textSubtitle;

    // If first phase not complete, show first phase counter
    if (firstPhaseTotal > 0 && firstPhaseRevealed < firstPhaseTotal) {
      return `${firstPhaseLabel}: ${firstPhaseRevealed}/${firstPhaseTotal}`;
    }

    // First phase complete, show second phase
    if (secondPhaseTotal > 0) {
      return `${secondPhaseLabel}: ${secondPhaseRevealed}/${secondPhaseTotal}`;
    }

    // If only one phase has entries
    if (firstPhaseTotal > 0) {
      return `${firstPhaseLabel}: ${firstPhaseRevealed}/${firstPhaseTotal}`;
    }

    return textSubtitle;
  };

  const getMedalClass = (index) => {
    // Only use medal gradient classes when all votes are applied (final results)
    if (!allVotesApplied) return "";
    if (index === 0) return "gold-bg";
    if (index === 1) return "silver-bg";
    if (index === 2) return "bronze-bg";
    return "";
  };

  // Get the custom top 3 color for a position (used during voting)
  const getTop3Color = (index) => {
    if (index === 0) return top1Color;
    if (index === 1) return top2Color;
    if (index === 2) return top3Color;
    return null;
  };

  // Get the positional translucent setting
  const getPositionalTranslucent = (index, totalCount) => {
    if (index === 0) return top1Translucent;
    if (index === 1) return top2Translucent;
    if (index === 2) return top3Translucent;
    if (index === totalCount - 1) return lastPlaceTranslucent;
    return false;
  };

  // Apply translucent effect to a hex color
  const applyTranslucency = (color, translucent) => {
    if (!translucent || !color) return color;
    // If it's a hex color, append opacity
    if (color.startsWith('#')) {
      return color.length <= 7 ? `${color}40` : color;
    }
    // If it's rgba, modify the alpha
    if (color.startsWith('rgba')) {
      return color.replace(/,\s*[\d.]+\)/, ', 0.25)');
    }
    return color;
  };

  // Effective grid spans: when custom voting width is on, derive the board spans
  // so the leaderboard adapts; otherwise use the configured spans (unchanged).
  // Force an even voting width so the two leaderboard columns are ALWAYS equal
  // ((12 - even) / 2 is an integer on both sides).
  const evenVotingSpan = votingWidthSpan % 2 === 0 ? votingWidthSpan : Math.max(2, votingWidthSpan - 1);
  const effVotingSpan = votingWidthCustom ? evenVotingSpan : votingColSpan;
  const effLeftSpan = votingWidthCustom ? (12 - evenVotingSpan) / 2 : leftColSpan;
  const effRightSpan = votingWidthCustom ? (12 - evenVotingSpan) / 2 : rightColSpan;

  // Background color for a voting-distribution bar (the rows inside the voting column)
  const voteBarColorToRgba = (color, alpha) => {
    if (!color) return color;
    if (color.startsWith('#')) {
      let hex = color.slice(1);
      if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    const m = color.match(/rgba?\(([^)]+)\)/i);
    if (m) {
      const [r, g, b] = m[1].split(',').map((s) => parseFloat(s));
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  };
  const getVoteBarBg = (isEvenRow) => {
    if (voteBarColorMode === 'original') return isEvenRow ? rowBgEven : rowBgOdd;
    const base = voteBarColorMode === 'dual' ? (isEvenRow ? voteBarColor1 : voteBarColor2) : voteBarColor1;
    const alpha = (voteBarTranslucent ? 0.25 : 1) * (voteBarOpacity / 100);
    return voteBarColorToRgba(base, alpha);
  };

  const getMedalIcon = (index) => {
    if (index < 3) {
      return <Trophy className="w-5 h-5 text-white" />;
    }
    return null;
  };

  const getPositionClass = (position) => {
    switch(position) {
      case "top-left": return "top-4 left-4";
      case "top-right": return "top-4 right-4";
      case "top-center": return "top-4 left-1/2 -translate-x-1/2";
      case "bottom-left": return "bottom-4 left-4";
      case "bottom-right": return "bottom-4 right-4";
      default: return "top-4 left-4";
    }
  };

  const mysteryHiddenSet = mysteryHiddenPoints === "12_10_8" ? [12, 10, 8] : mysteryHiddenPoints === "12_10" ? [12, 10] : [12];

  const half = Math.floor(leaderboard.length / 2);
  const leftColumn = leaderboard.slice(0, half);
  const rightColumn = leaderboard.slice(half);
  
  // Get the active font for each section, supporting individual custom fonts
  const getActiveFont = (fontType, section) => {
    if (fontType === "Custom") {
      const customFonts = {
        title: { name: customFontTitleName, loaded: customFontsLoaded.title },
        subtitle: { name: customFontSubtitleName, loaded: customFontsLoaded.subtitle },
        country: { name: customFontCountryName, loaded: customFontsLoaded.country },
        points: { name: customFontPointsName, loaded: customFontsLoaded.points },
        voting: { name: customFontVotingName, loaded: customFontsLoaded.voting },
        votingSecondary: { name: customFontVotingSecondaryName, loaded: customFontsLoaded.votingSecondary }
      };
      const custom = customFonts[section];
      if (custom && custom.name && custom.loaded) {
        // Quote the name to handle special characters (e.g. dots) in font names
        return `"${custom.name}"`;
      }
      // Fallback if custom font not loaded
      return section === 'title' ? 'Unbounded' : 
             section === 'points' ? 'Chivo Mono' : 'Inter';
    }
    return fontType;
  };

  const vizCtx = {
    audioUnlocked, setAudioUnlocked, leaderboard, setLeaderboard, currentVoter, setCurrentVoter,
    currentVote, setCurrentVote, showAnimation, setShowAnimation, countries, setCountries,
    voteApplied, setVoteApplied, backgroundVideo, setBackgroundVideo, bgVideoBlur, setBgVideoBlur, bgVideoBrightness, setBgVideoBrightness, bgVideoOpacity, setBgVideoOpacity, fontTitle, setFontTitle,
    fontSubtitle, setFontSubtitle, fontCountry, setFontCountry, fontPoints, setFontPoints,
    fontVoting, setFontVoting, customFontTitleUrl, setCustomFontTitleUrl, customFontTitleName, setCustomFontTitleName,
    customFontSubtitleUrl, setCustomFontSubtitleUrl, customFontSubtitleName, setCustomFontSubtitleName, customFontCountryUrl, setCustomFontCountryUrl,
    customFontCountryName, setCustomFontCountryName, customFontPointsUrl, setCustomFontPointsUrl, customFontPointsName, setCustomFontPointsName,
    customFontVotingUrl, setCustomFontVotingUrl, customFontVotingName, setCustomFontVotingName, customFontsLoaded, setCustomFontsLoaded,
    primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor, tertiaryColor, setTertiaryColor,
    logoUrl, setLogoUrl, logoPosition, setLogoPosition, logoSize, setLogoSize,
    logoZIndex, setLogoZIndex, logoOpacity, setLogoOpacity, extraImages, setExtraImages,
    fontSizeTitle, setFontSizeTitle, fontSizeSubtitle, setFontSizeSubtitle, fontSizeCountry, setFontSizeCountry,
    fontSizePoints, setFontSizePoints, fontSizeVoting, setFontSizeVoting, rowBgEven, setRowBgEven,
    rowBgOdd, setRowBgOdd, rowBgEvenTranslucent, setRowBgEvenTranslucent, rowBgOddTranslucent, setRowBgOddTranslucent, defaultArtistPhoto, setDefaultArtistPhoto, artistPhotoSizeMode, setArtistPhotoSizeMode,
    artistPhotoSizePx, setArtistPhotoSizePx, textTitle, setTextTitle, textSubtitle, setTextSubtitle,
    textVotingNow, setTextVotingNow, textWaiting, setTextWaiting, sound12PointsUrl, setSound12PointsUrl,
    confettiEnabled, setConfettiEnabled, confettiColors, setConfettiColors, top1Color, setTop1Color,
    top2Color, setTop2Color, top3Color, setTop3Color, lastPlaceColor, setLastPlaceColor,
    top1Translucent, setTop1Translucent, top2Translucent, setTop2Translucent, top3Translucent, setTop3Translucent,
    lastPlaceTranslucent, setLastPlaceTranslucent, positionBadgeTranslucent, setPositionBadgeTranslucent, votingColumnCenter, setVotingColumnCenter, leftColSpan, setLeftColSpan,
    rightColSpan, setRightColSpan, votingColSpan, setVotingColSpan, columnGap, setColumnGap,
    subtitleCounterEnabled, setSubtitleCounterEnabled, subtitlePhaseOrder, setSubtitlePhaseOrder, subtitleJuryLabel, setSubtitleJuryLabel,
    subtitleTelevoteLabel, setSubtitleTelevoteLabel, revealedVoters, setRevealedVoters, mystery12Text, setMystery12Text,
    twelveRevealed, setTwelveRevealed, voteDisplayColumns, setVoteDisplayColumns, showVoterBorder, setShowVoterBorder,
    showJuryBadge, setShowJuryBadge, fontVotingSecondary, setFontVotingSecondary, customFontVotingSecondaryUrl, setCustomFontVotingSecondaryUrl,
    customFontVotingSecondaryName, setCustomFontVotingSecondaryName, overlay12Enabled, setOverlay12Enabled, overlay12DurationMode, setOverlay12DurationMode,
    overlay12DurationSeconds, setOverlay12DurationSeconds, overlay12BgStyle, setOverlay12BgStyle, overlay12BgColor, setOverlay12BgColor,
    overlay12BgOpacity, setOverlay12BgOpacity, overlay12TextReceives, setOverlay12TextReceives, overlay12UseAdminColors, setOverlay12UseAdminColors, overlay12ArrowColor, overlay12ArrowThickness, overlay12ArrowHeadSize, setOverlay12ArrowColor,
    overlay12StyleCustom, setOverlay12StyleCustom, overlay12FontTitle, setOverlay12FontTitle, overlay12FontCountry, setOverlay12FontCountry,
    overlay12FontSecondary, setOverlay12FontSecondary, overlay12SizeScale, setOverlay12SizeScale, overlay12Visible, setOverlay12Visible,
    overlay12RecipientId, setOverlay12RecipientId, overlay12Stage, setOverlay12Stage, winnerOverlayEnabled, setWinnerOverlayEnabled,
    winnerOverlayDurationMode, setWinnerOverlayDurationMode, winnerOverlayDurationSeconds, setWinnerOverlayDurationSeconds, winnerOverlayBgStyle, setWinnerOverlayBgStyle,
    winnerOverlayBgColor, setWinnerOverlayBgColor, winnerOverlayBgOpacity, setWinnerOverlayBgOpacity, winnerOverlayTitleText, setWinnerOverlayTitleText,
    winnerOverlaySubtitleText, setWinnerOverlaySubtitleText, winnerOverlayUseAdminColors, setWinnerOverlayUseAdminColors, winnerOverlayAccentColor, winnerOverlayIcon, winnerOverlayIconUrl, winnerOverlayShowConfetti, setWinnerOverlayShowConfetti,
    winnerOverlayShowPoints, setWinnerOverlayShowPoints, winnerOverlayStyleCustom, setWinnerOverlayStyleCustom, winnerOverlayFontTitle, setWinnerOverlayFontTitle,
    winnerOverlayFontCountry, setWinnerOverlayFontCountry, winnerOverlayFontSecondary, setWinnerOverlayFontSecondary, winnerOverlaySizeScale, setWinnerOverlaySizeScale,
    winnerOverlayVisible, setWinnerOverlayVisible, winnerOverlayWinnerId, setWinnerOverlayWinnerId, winnerOverlayWinnerPoints, setWinnerOverlayWinnerPoints,
    allVotesApplied, setAllVotesApplied, orientation, setOrientation, showRotateHint, setShowRotateHint,
    videoRef, audioRef, sound12PointsUrlRef, confettiEnabledRef, confettiColorsRef, prevTwelveFromServerRef,
    prevOverlayVisibleRef, overlayAutoCloseTimerRef, overlayStageTimerRef, pollRef, fetchCurrentVoteRef, lastCountriesRef,
    lastBgConfigRef, lastRevealedRef, prevWinnerVisibleRef, winnerAutoCloseTimerRef, winnerConfettiIntervalRef, playVoterVideoWithSound,
    triggerTwelveRevealEffects, voterCountryId, voterShowAnimation, fetchLeaderboard, fetchCurrentVoter, fetchCountries,
    fetchBackground, fetchCurrentVote, fetchVotingStatus, fetchRevealedVoters, triggerWinnerConfetti, stopWinnerConfetti,
    fetchWinnerOverlay, computeDynamicSubtitle, getMedalClass, getTop3Color, getPositionalTranslucent, applyTranslucency,
    getMedalIcon, getPositionClass, half, leftColumn, rightColumn, getActiveFont,
    top6Visible, top6Entries, top6TitleText, top6StyleCustom, top6FontTitle, top6FontCountry,
  };

  const visualizerContent = (
    <VisualizerContext.Provider value={vizCtx}>
    <div 
      className={`p-6 relative overflow-hidden ${orientation.mobile ? "w-full h-full" : "min-h-screen"}`}
      style={{
        background: '#09090B',
        '--stage-h': orientation.mobile ? `${DESIGN_H}px` : '100vh'
      }}
      data-testid="visualizer-screen"
    >
      {/* Discreet audio badge: only when the browser blocked autoplay and a 12-points sound is configured */}
      {!audioUnlocked && (sound12PointsUrl || soundWinnerUrl) && (
        <button
          type="button"
          className="fixed bottom-4 right-4 z-[10000] flex items-center gap-2 px-3 py-2 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 text-white text-xs backdrop-blur-md transition-colors"
          onClick={() => {
            const el = audioRef.current || winnerAudioRef.current;
            if (el) {
              el.play().then(() => {
                el.pause();
                el.currentTime = 0;
                setAudioUnlocked(true);
              }).catch(() => {});
            }
          }}
          data-testid="audio-unlock-overlay"
        >
          <VolumeX className="w-4 h-4" />
          Activar sonido
        </button>
      )}
      {/* Background video or image */}
      {backgroundVideo ? (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: bgVideoOpacity / 100, filter: `blur(${bgVideoBlur}px) brightness(${bgVideoBrightness}%)` }}
        >
          <source src={resolveUrl(backgroundVideo)} type="video/mp4" />
        </video>
      ) : (
        <div 
          className="absolute inset-0"
          style={{
            opacity: bgVideoOpacity / 100,
            backgroundImage: `url('https://images.unsplash.com/photo-1721623777765-1381ba32859c?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: `blur(${bgVideoBlur}px) brightness(${bgVideoBrightness}%)`
          }}
        />
      )}

      {/* Logo */}
      {logoUrl && (
        <div
          className={`absolute ${(logoX != null && logoY != null) ? '' : getPositionClass(logoPosition)}`}
          style={{ zIndex: logoZIndex, opacity: logoOpacity / 100, ...((logoX != null && logoY != null) ? { left: `${logoX}%`, top: `${logoY}%` } : {}) }}
        >
          <img 
            src={resolveUrl(logoUrl)} 
            alt="Logo" 
            className="w-auto object-contain drop-shadow-2xl"
            style={{ height: `${logoSize}px` }}
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}

      {/* Extra Images */}
      {extraImages.map((img, i) => img.url && (
        <div
          key={`extra-img-${i}`}
          className={`absolute ${(img.x != null && img.y != null) ? '' : getPositionClass(img.position)}`}
          style={{ zIndex: img.zIndex, opacity: (img.opacity ?? 100) / 100, ...((img.x != null && img.y != null) ? { left: `${img.x}%`, top: `${img.y}%` } : {}) }}
        >
          <img 
            src={resolveUrl(img.url)} 
            alt={`Extra ${i + 1}`} 
            className="w-auto object-contain drop-shadow-2xl"
            style={{ height: `${img.size}px` }}
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      ))}

      {/* Back button */}
      <div className="absolute top-4 left-4 z-50">
        <Button
          data-testid="btn-back-visualizer"
          onClick={() => window.location.href = '/'}
          variant="ghost"
          className="bg-zinc-900/50 backdrop-blur-md text-white hover:bg-zinc-800/50"
        >
          <Home className="w-4 h-4 mr-2" />
          Inicio
        </Button>
      </div>

      {/* Header */}
      <div className="relative z-10 text-center mb-8">
        <h1 
          className="font-black tracking-tight mb-2 gradient-text inline-block"
          style={{
            fontFamily: getActiveFont(fontTitle, "title"), fontStyle: fontTitleItalic ? 'italic' : 'normal',
            fontSize: `${fontSizeTitle}px`,
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor}, ${tertiaryColor})`
          }}
        >
          {textTitle}
        </h1>
        <p 
          className="text-zinc-400 font-medium"
          style={{
            fontFamily: getActiveFont(fontSubtitle, "subtitle"), fontStyle: fontSubtitleItalic ? 'italic' : 'normal',
            fontSize: `${fontSizeSubtitle}px`
          }}
          data-testid="visualizer-subtitle"
        >
          {computeDynamicSubtitle()}
        </p>
      </div>

      {/* Main Grid */}
      <div 
        className="relative z-10 grid grid-cols-1 lg:grid-cols-12"
        style={{ gap: `${columnGap * 4}px`, height: 'calc(var(--stage-h, 100vh) - 200px)', ...(orientation.mobile ? { gridTemplateColumns: 'repeat(12, minmax(0, 1fr))' } : {}) }}
        data-testid="scoreboard-grid"
      >
        {/* Left Leaderboard */}
        <div className={`lg:col-span-${leftColSpan} space-y-1.5 flex flex-col`} style={{ gridColumn: `span ${effLeftSpan}` }} data-testid="leaderboard-left">
          {leftColumn.map((country, index) => {
            const globalIndex = index;
            const isEven = globalIndex % 2 === 0;
            const isTop3 = globalIndex < 3;
            const isLastPlace = globalIndex === leaderboard.length - 1;
            const medalClass = getMedalClass(globalIndex);
            const customColor = getTop3Color(globalIndex);
            const useCustomColor = isTop3 && !allVotesApplied;
            const showBadge = country.show_position_badge !== false;
            return (
              <motion.div
                key={country.id}
                layout
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 50, damping: 15 }}
                className="flex items-stretch gap-2"
                data-testid={`country-rank-${globalIndex + 1}`}
              >
                {showBadge ? (
                  <div
                    className={`flex items-center justify-center rounded-xl font-black text-white shrink-0 ${
                      isTop3 && allVotesApplied ? `${medalClass} shadow-lg` : ''
                    }`}
                    style={{
                      width: `${Math.max(fontSizePoints * 2, 44)}px`,
                      fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal',
                      fontSize: `${fontSizePoints}px`,
                      ...(isLastPlace
                        ? { backgroundColor: applyTranslucency(lastPlaceColor, positionBadgeTranslucent), boxShadow: positionBadgeTranslucent ? 'none' : `0 4px 15px ${lastPlaceColor}60` }
                        : useCustomColor
                          ? { backgroundColor: applyTranslucency(customColor, positionBadgeTranslucent), boxShadow: positionBadgeTranslucent ? 'none' : `0 4px 15px ${customColor}60` }
                          : !isTop3 || !allVotesApplied
                            ? { backgroundColor: applyTranslucency(isEven ? rowBgEven : rowBgOdd, rowBadgeTranslucent) }
                            : {})
                    }}
                    data-testid={`position-badge-${globalIndex + 1}`}
                  >
                    {globalIndex + 1}
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-center shrink-0 font-black text-white/40"
                    style={{
                      width: `${Math.max(fontSizePoints * 2, 44)}px`,
                      fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal',
                      fontSize: `${fontSizePoints}px`
                    }}
                    data-testid={`position-badge-${globalIndex + 1}`}
                  >
                    {globalIndex + 1}
                  </div>
                )}
                <div
                  className={`flex-1 p-2 rounded-xl border backdrop-blur-md ${
                    isTop3 && allVotesApplied && !country.bar_color
                      ? `${medalClass} border-white/30 shadow-xl`
                      : isTop3 && useCustomColor && !country.bar_color
                        ? 'border-white/30 shadow-xl'
                        : 'border-white/10'
                  }`}
                  style={(() => {
                    const t = country.bar_translucent;
                    const pt = getPositionalTranslucent(globalIndex, leaderboard.length);
                    if (country.bar_color)
                      return { backgroundColor: applyTranslucency(country.bar_color, t) };
                    if (isLastPlace)
                      return { backgroundColor: applyTranslucency(lastPlaceColor, pt) };
                    if (useCustomColor)
                      return { backgroundColor: applyTranslucency(customColor, pt), borderColor: `${customColor}50` };
                    if (!(isTop3 && allVotesApplied))
                      return { backgroundColor: applyTranslucency(isEven ? rowBgEven : rowBgOdd, isEven ? rowBgEvenTranslucent : rowBgOddTranslucent) };
                    return {};
                  })()}
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={resolveUrl(country.flag_url)}
                      alt={country.name}
                      className="w-14 h-10 object-contain"
                      onError={(e) => e.target.src = 'https://placehold.co/100x70/000/FFF?text=Flag'}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate" style={{ fontFamily: getActiveFont(fontCountry, "country"), fontStyle: fontCountryItalic ? 'italic' : 'normal', fontSize: `${fontSizeCountry}px` }}>{country.name}</h3>
                    </div>
                    <div className="font-bold text-white" style={{ fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal', fontSize: `${fontSizePoints}px` }}>
                      {country.total_points}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Center Voter Display (when votingColumnCenter is true) */}
        {votingColumnCenter && (
          <div 
            className="flex flex-col justify-start items-stretch bg-zinc-900/50 rounded-2xl border border-white/10 backdrop-blur-xl overflow-hidden"
            style={{ gridColumn: `span ${effVotingSpan}`, order: 0 }}
            data-testid="voting-column-center"
          >
            <AnimatePresence mode="wait">
              {showAnimation && currentVote && currentVoter ? (
                <motion.div
                  key={currentVote.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 100, damping: 15 }}
                  className="w-full h-full flex flex-col"
                  data-testid="current-voter-display"
                >
                  {countries.find(c => c.id === currentVoter.country_id) && (() => {
                    const country = countries.find(c => c.id === currentVoter.country_id);
                    const mediaUrl = resolveUrl(country.artist_photo_url) || 
                                     (defaultArtistPhoto ? resolveUrl(defaultArtistPhoto) : null);
                    const isVideo = mediaUrl && /\.(mp4|webm|mov|avi|ogg)$/i.test(mediaUrl);
                    return (
                      <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="w-full flex justify-center pt-3 px-2"
                      >
                        <div className="w-28 h-28 rounded-full overflow-hidden border-2" style={{ borderColor: primaryColor }}>
                          {isVideo ? (
                            <video ref={playVoterVideoWithSound} src={mediaUrl} autoPlay playsInline className="w-full h-full object-cover" />
                          ) : (
                            <img
                              src={mediaUrl || 'https://placehold.co/200x200/27272a/FFF?text=?'}
                              alt={currentVote.from_country_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = defaultArtistPhoto ? resolveUrl(defaultArtistPhoto) : 'https://placehold.co/200x200/27272a/FFF?text=?';
                              }}
                            />
                          )}
                        </div>
                      </motion.div>
                    );
                  })()}
                  <div className="px-2 py-2 flex flex-col flex-1 overflow-hidden">
                    <motion.div
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="text-center mb-2"
                    >
                      <p className="text-zinc-400 text-[10px] uppercase tracking-wider" style={{ fontFamily: getActiveFont(fontVotingSecondary, "votingSecondary"), fontStyle: fontVotingSecondaryItalic ? 'italic' : 'normal' }}>{textVotingNow}</p>
                      <h2 className="font-black text-white text-sm leading-tight mt-1" style={{ fontFamily: getActiveFont(fontVoting, "voting"), fontStyle: fontVotingItalic ? 'italic' : 'normal' }}>
                        {currentVote.from_country_name}
                      </h2>
                      {(() => {
                        const voter = countries.find(c => c.id === currentVoter.country_id);
                        if (voter && voter.flag_url && voter.flag_url.trim() !== '') {
                          return <img src={resolveUrl(voter.flag_url)} alt="" className="w-12 h-8 object-contain mx-auto mt-1" onError={(e) => e.target.style.display='none'} />;
                        }
                        if (showJuryBadge && voter && voter.flag_text && voter.flag_text.trim() !== '') {
                          return <span className="text-[10px] font-bold tracking-wider mt-1 block" style={{ color: primaryColor, fontFamily: getActiveFont(fontVotingSecondary, "votingSecondary"), fontStyle: fontVotingSecondaryItalic ? 'italic' : 'normal' }}>{voter.flag_text}</span>;
                        }
                        return null;
                      })()}
                    </motion.div>
                    <div className="w-full h-px bg-white/10 mb-2" />
                    <div 
                      className={`overflow-y-auto flex-1 custom-scrollbar ${voteDisplayColumns === 'double' ? 'grid grid-cols-2 gap-1' : 'space-y-1'}`}
                    >
                      {Object.entries(currentVote.votes)
                        .sort((a, b) => b[1] - a[1])
                        .map(([countryId, points], index) => {
                          const country = countries.find(c => c.id === countryId);
                          const is12Points = points === 12;
                          const shouldHide12 = mysteryHiddenSet.includes(points) && !twelveRevealed && !voteApplied;
                          return (
                            <motion.div
                              key={countryId}
                              initial={{ x: 30, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: 0.4 + index * 0.05 }}
                              className="rounded-md px-1.5 py-1 border border-white/5 flex items-center justify-between gap-1"
                              style={{ backgroundColor: getVoteBarBg(index % 2 === 0) }}
                            >
                              {shouldHide12 ? (
                                <>
                                  <span className="text-[11px] truncate flex-1 font-bold tracking-wider text-white" style={{ fontFamily: getActiveFont(fontCountry, "country"), fontStyle: fontCountryItalic ? 'italic' : 'normal' }}>
                                    {mystery12Text}
                                  </span>
                                  <span className="font-black text-xs shrink-0" style={{ color: primaryColor, fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal' }}>
                                    {points}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1 flex-1 min-w-0">
                                    {is12Points && country && (
                                      <motion.img
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                        src={resolveUrl(country.flag_url)}
                                        alt={country.name}
                                        className="w-6 h-4 object-contain shrink-0"
                                        onError={(e) => e.target.style.display='none'}
                                      />
                                    )}
                                    <span className="text-white text-[11px] truncate" style={{ fontFamily: getActiveFont(fontCountry, "country"), fontStyle: fontCountryItalic ? 'italic' : 'normal' }}>
                                      {country?.name || '?'}
                                    </span>
                                  </div>
                                  <motion.span
                                    initial={is12Points && twelveRevealed ? { scale: 0 } : false}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                                    className="font-black text-xs shrink-0"
                                    style={{ color: primaryColor, fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal' }}
                                  >
                                    {points}
                                  </motion.span>
                                </>
                              )}
                            </motion.div>
                          );
                        })}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center p-3 flex flex-col items-center justify-start h-full pt-6"
                >
                  <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center mb-2">
                    <Trophy className="w-7 h-7 text-zinc-600" />
                  </div>
                  <p className="text-zinc-500 text-xs">{textWaiting}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Right Leaderboard */}
        <div className={`lg:col-span-${rightColSpan} space-y-1.5 flex flex-col`} style={{ gridColumn: `span ${effRightSpan}` }} data-testid="leaderboard-right">
          {rightColumn.map((country, index) => {
            const globalIndex = half + index;
            const isEven = globalIndex % 2 === 0;
            const isTop3 = globalIndex < 3;
            const isLastPlace = globalIndex === leaderboard.length - 1;
            const medalClass = getMedalClass(globalIndex);
            const customColor = getTop3Color(globalIndex);
            const useCustomColor = isTop3 && !allVotesApplied;
            const showBadge = country.show_position_badge !== false;
            return (
              <motion.div
                key={country.id}
                layout
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 50, damping: 15 }}
                className="flex items-stretch gap-2"
                data-testid={`country-rank-${globalIndex + 1}`}
              >
                {showBadge ? (
                  <div
                    className={`flex items-center justify-center rounded-xl font-black text-white shrink-0 ${
                      isTop3 && allVotesApplied ? `${medalClass} shadow-lg` : ''
                    }`}
                    style={{
                      width: `${Math.max(fontSizePoints * 2, 44)}px`,
                      fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal',
                      fontSize: `${fontSizePoints}px`,
                      ...(isLastPlace
                        ? { backgroundColor: applyTranslucency(lastPlaceColor, positionBadgeTranslucent), boxShadow: positionBadgeTranslucent ? 'none' : `0 4px 15px ${lastPlaceColor}60` }
                        : useCustomColor
                          ? { backgroundColor: applyTranslucency(customColor, positionBadgeTranslucent), boxShadow: positionBadgeTranslucent ? 'none' : `0 4px 15px ${customColor}60` }
                          : !isTop3 || !allVotesApplied
                            ? { backgroundColor: applyTranslucency(isEven ? rowBgEven : rowBgOdd, rowBadgeTranslucent) }
                            : {})
                    }}
                    data-testid={`position-badge-${globalIndex + 1}`}
                  >
                    {globalIndex + 1}
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-center shrink-0 font-black text-white/40"
                    style={{
                      width: `${Math.max(fontSizePoints * 2, 44)}px`,
                      fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal',
                      fontSize: `${fontSizePoints}px`
                    }}
                    data-testid={`position-badge-${globalIndex + 1}`}
                  >
                    {globalIndex + 1}
                  </div>
                )}
                <div
                  className={`flex-1 p-2 rounded-xl border backdrop-blur-md ${
                    isTop3 && allVotesApplied && !country.bar_color
                      ? `${medalClass} border-white/30 shadow-xl`
                      : isTop3 && useCustomColor && !country.bar_color
                        ? 'border-white/30 shadow-xl'
                        : 'border-white/10'
                  }`}
                  style={(() => {
                    const t = country.bar_translucent;
                    const pt = getPositionalTranslucent(globalIndex, leaderboard.length);
                    if (country.bar_color)
                      return { backgroundColor: applyTranslucency(country.bar_color, t) };
                    if (isLastPlace)
                      return { backgroundColor: applyTranslucency(lastPlaceColor, pt) };
                    if (useCustomColor)
                      return { backgroundColor: applyTranslucency(customColor, pt), borderColor: `${customColor}50` };
                    if (!(isTop3 && allVotesApplied))
                      return { backgroundColor: applyTranslucency(isEven ? rowBgEven : rowBgOdd, isEven ? rowBgEvenTranslucent : rowBgOddTranslucent) };
                    return {};
                  })()}
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={resolveUrl(country.flag_url)}
                      alt={country.name}
                      className="w-14 h-10 object-contain"
                      onError={(e) => e.target.src = 'https://placehold.co/100x70/000/FFF?text=Flag'}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate" style={{ fontFamily: getActiveFont(fontCountry, "country"), fontStyle: fontCountryItalic ? 'italic' : 'normal', fontSize: `${fontSizeCountry}px` }}>{country.name}</h3>
                    </div>
                    <div className="font-bold text-white" style={{ fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal', fontSize: `${fontSizePoints}px` }}>
                      {country.total_points}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Side Voter Display (when votingColumnCenter is false) */}
        {!votingColumnCenter && (
          <div 
            className="flex flex-col justify-start items-stretch bg-zinc-900/50 rounded-3xl border border-white/10 backdrop-blur-xl overflow-hidden"
            style={{ gridColumn: `span ${effVotingSpan}` }}
            data-testid="voting-column-side"
          >
          <AnimatePresence mode="wait">
            {showAnimation && currentVote && currentVoter ? (
              <motion.div
                key={currentVote.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 100, damping: 15 }}
                className="w-full h-full flex flex-col"
                data-testid="current-voter-display"
              >
                {/* Artist Photo/Video - Square, adaptive or fixed size */}
                {countries.find(c => c.id === currentVoter.country_id) && (() => {
                  const country = countries.find(c => c.id === currentVoter.country_id);
                  const mediaUrl = resolveUrl(country.artist_photo_url) || 
                                   (defaultArtistPhoto ? resolveUrl(defaultArtistPhoto) : null);
                  // Si el país no tiene foto/vídeo de artista (ni hay foto por defecto),
                  // no reservamos el recuadro cuadrado: así los votos suben y se ven bien.
                  if (!mediaUrl) return null;
                  const isVideo = mediaUrl && /\.(mp4|webm|mov|avi|ogg)$/i.test(mediaUrl);
                  return (
                    <VoterArtistMedia
                      key={mediaUrl}
                      mediaUrl={mediaUrl}
                      isVideo={isVideo}
                      sizeMode={artistPhotoSizeMode}
                      sizePx={artistPhotoSizePx}
                      showVoterBorder={showVoterBorder}
                      primaryColor={primaryColor}
                      defaultArtistPhoto={defaultArtistPhoto}
                      resolveUrl={resolveUrl}
                      fromName={currentVote.from_country_name}
                      videoRef={playVoterVideoWithSound}
                    />
                  );
                })()}

                {/* Voting Info - Below Photo */}
                <div className="p-4 flex flex-col flex-1">
                  <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-center mb-3"
                  >
                    <p className="text-zinc-400 mb-1" style={{ fontFamily: getActiveFont(fontVotingSecondary, "votingSecondary"), fontStyle: fontVotingSecondaryItalic ? 'italic' : 'normal', fontSize: `${fontSizeVoting * 0.7}px` }}>{textVotingNow}</p>
                    <h2 className="font-black text-white mb-2" style={{ fontFamily: getActiveFont(fontVoting, "voting"), fontStyle: fontVotingItalic ? 'italic' : 'normal', fontSize: `${fontSizeVoting}px` }}>
                      {currentVote.from_country_name}
                    </h2>
                    {(() => {
                      const voter = countries.find(c => c.id === currentVoter.country_id);
                      if (voter && voter.flag_url && voter.flag_url.trim() !== '') {
                        return (
                          <img
                            src={resolveUrl(voter.flag_url)}
                            alt={currentVote.from_country_name}
                            className="w-24 h-14 object-contain mx-auto"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        );
                      }
                      if (showJuryBadge && voter && voter.flag_text && voter.flag_text.trim() !== '') {
                        return (
                          <div 
                            className="px-4 py-2 rounded-lg mx-auto inline-block"
                            style={{ 
                              backgroundColor: `${primaryColor}20`,
                              border: `2px solid ${primaryColor}`,
                            }}
                          >
                            <span 
                              className="font-bold text-sm tracking-wider"
                              style={{ color: primaryColor, fontFamily: getActiveFont(fontVotingSecondary, "votingSecondary"), fontStyle: fontVotingSecondaryItalic ? 'italic' : 'normal' }}
                            >
                              {voter.flag_text}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </motion.div>

                  <div 
                    className={`overflow-y-auto flex-1 ${voteDisplayColumns === 'double' ? 'grid grid-cols-2 gap-1.5' : 'space-y-1.5'}`}
                  >
                  {Object.entries(currentVote.votes)
                    .sort((a, b) => b[1] - a[1])
                    .map(([countryId, points], index) => {
                      const country = countries.find(c => c.id === countryId);
                      const is12Points = points === 12;
                      const shouldHide12 = mysteryHiddenSet.includes(points) && !twelveRevealed && !voteApplied;
                      const isEven = index % 2 === 0;
                      
                      return (
                        <motion.div
                          key={countryId}
                          initial={{ x: 100, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          className="rounded-lg p-2 border border-white/10 flex items-center justify-between"
                          style={{ backgroundColor: getVoteBarBg(isEven) }}
                          data-testid={`vote-detail-${countryId}`}
                        >
                          {shouldHide12 ? (
                            <>
                              <div className="flex items-center gap-2 flex-1">
                                <span className="font-bold tracking-wider text-white" style={{ fontFamily: getActiveFont(fontCountry, "country"), fontStyle: fontCountryItalic ? 'italic' : 'normal', fontSize: `${fontSizeCountry}px` }}>
                                  {mystery12Text}
                                </span>
                              </div>
                              <span className="font-black" style={{ color: primaryColor, fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal', fontSize: `${fontSizePoints}px` }}>
                                {points}
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                {is12Points && country ? (
                                  <motion.img
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                    src={resolveUrl(country.flag_url)}
                                    alt={country.name}
                                    className="w-12 h-8 object-contain"
                                    onError={(e) => e.target.src = 'https://placehold.co/80x55/000/FFF?text=Flag'}
                                  />
                                ) : (
                                  <img
                                    src={resolveUrl(country?.flag_url)}
                                    alt={country?.name}
                                    className="w-12 h-8 object-contain"
                                    onError={(e) => e.target.src = 'https://placehold.co/80x55/000/FFF?text=Flag'}
                                  />
                                )}
                                <motion.span
                                  initial={is12Points && twelveRevealed ? { opacity: 0, x: -10 } : false}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                  className="text-white font-semibold"
                                  style={{ fontFamily: getActiveFont(fontCountry, "country"), fontStyle: fontCountryItalic ? 'italic' : 'normal', fontSize: `${fontSizeCountry}px` }}
                                >
                                  {country?.name || 'Desconocido'}
                                </motion.span>
                              </div>
                              <motion.div
                                initial={is12Points && twelveRevealed ? { scale: 0 } : { scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: is12Points ? 0.2 : 0.6 + index * 0.1, type: "spring" }}
                                className="font-black"
                                style={{ 
                                  color: primaryColor,
                                  fontFamily: getActiveFont(fontPoints, "points"), fontStyle: fontPointsItalic ? 'italic' : 'normal',
                                  fontSize: `${fontSizePoints}px`
                                }}
                              >
                                {points}
                              </motion.div>
                            </>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center p-4 flex flex-col items-center justify-start h-full pt-8"
              >
                <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mb-3">
                  <Trophy className="w-10 h-10 text-zinc-600" />
                </div>
                <p className="text-zinc-500 text-base">{textWaiting}</p>
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(39, 39, 42, 0.5);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(236, 72, 153, 0.5);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(236, 72, 153, 0.7);
        }
      `}</style>
      
      {/* 12-points Reveal Full-Screen Overlay */}
      <TwelvePointsOverlay />

      {/* Winner Overlay */}
      <WinnerOverlay />
      <Top6Overlay />

      {/* Hidden audio element for 12 points sound */}
      {sound12PointsUrl && (
        <audio 
          ref={audioRef}
          src={resolveUrl(sound12PointsUrl)}
          preload="auto"
        />
      )}
      {/* Hidden audio element for winner sound */}
      {soundWinnerUrl && (
        <audio
          ref={winnerAudioRef}
          src={resolveUrl(soundWinnerUrl)}
          preload="auto"
        />
      )}
    </div>
    </VisualizerContext.Provider>
  );

  // On mobile portrait, rotate the whole visualizer 90deg so it reads as landscape.
  // On mobile landscape or desktop, render normally.
  // ---- Mobile: render the desktop-sized stage scaled to fit (and rotated in portrait) ----
  if (orientation.mobile) {
    const stage = (
      <div
        style={{
          position: 'absolute',
          left: `${orientation.offsetX}px`,
          top: `${orientation.offsetY}px`,
          width: `${DESIGN_W}px`,
          height: `${DESIGN_H}px`,
          transformOrigin: 'top left',
          transform: `scale(${orientation.scale})`,
        }}
        data-testid="visualizer-stage"
      >
        {visualizerContent}
      </div>
    );

    const rotateHint = (
      <AnimatePresence>
        {showRotateHint && (
          <motion.div
            key="rotate-hint"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
            onClick={() => setShowRotateHint(false)}
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/75 backdrop-blur-sm px-8 cursor-pointer"
            data-testid="rotate-hint"
          >
            <div className="text-center max-w-xs">
              <motion.div
                animate={{ rotate: [0, 90, 90, 0] }}
                transition={{ duration: 2.2, times: [0, 0.4, 0.7, 1], repeat: Infinity, ease: 'easeInOut' }}
                className="inline-flex items-center justify-center mb-5"
              >
                <Smartphone className="w-16 h-16 text-pink-400" />
              </motion.div>
              <p className="text-white text-xl font-unbounded font-semibold mb-2">Gira tu móvil</p>
              <p className="text-zinc-300 text-sm">Ponlo en horizontal para una mejor experiencia 🎶</p>
              <p className="text-zinc-500 text-xs mt-4">Toca para cerrar</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );

    if (orientation.rotate) {
      return (
        <div style={{ position: 'fixed', inset: 0, background: '#09090B', overflow: 'hidden' }} data-testid="visualizer-rotate-wrapper">
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${orientation.h}px`,
              height: `${orientation.w}px`,
              transformOrigin: 'top left',
              transform: `rotate(90deg) translateY(-${orientation.w}px)`,
            }}
          >
            {stage}
          </div>
          {rotateHint}
        </div>
      );
    }

    return (
      <div style={{ position: 'fixed', inset: 0, background: '#09090B', overflow: 'hidden' }} data-testid="visualizer-scale-wrapper">
        {stage}
      </div>
    );
  }

  return visualizerContent;
};

export default Visualizer;
